import React, { lazy, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  InputBase,
  CircularProgress,
} from '@mui/material';
import {
  Train as TrainIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import UserAccountMenu from './UserAccountMenu';
import MountWhenOpened from '../common/MountWhenOpened';
import Globalicon from '../../assets/globalicon2.png';
import flight_icon_header from '../../assets/airplane.svg';
import searchNormalIcon from '../../assets/search-normal.svg';
import HeritageDestinationSearchDropdown from './HeritageDestinationSearchDropdown';
import {
  useHeritageDestinationSearch,
  shouldShowHeritageSearchDropdown,
} from '../../hooks/useHeritageDestinationSearch';
import { useHomepageTabPress } from './useHomepageTabPress';
import './styles/YovoTripHeader.css';

const RegionalSettingsModal = lazy(() => import('../Homepage/RegionalSettingsModal'));

/* Hoisted so tab identity stays stable. Defining this inside YovoTripHeader made
   every homepage update a new component type, which remounted the tabs and
   dropped the click that landed while those updates were still running. */
const NavigationItem = ({ icon, label, onClick, active = false, showBrandChrome = false }) => {
  const labelColor = active
    ? (showBrandChrome ? '#FFFFFF' : '#CF2048')
    : (showBrandChrome ? '#FBFBFB' : 'var(--yovo-grey-900)');
  return (
    <Box
      role="tab"
      aria-selected={active}
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        cursor: 'pointer',
        textDecoration: 'none',
      }}
    >
      {icon}
      <Typography sx={{
        color: labelColor,
        fontFamily: 'var(--yovo-font-primary)',
        fontSize: { sm: '11px', lg: '12px' },
        fontWeight: active ? 800 : 700,
        lineHeight: '14px',
        letterSpacing: { sm: '0.1px', lg: '0.25px' },
        whiteSpace: 'nowrap',
        transition: 'color 0.3s ease',
      }}>
        {label}
      </Typography>
    </Box>
  );
};

const YovoTripHeader = ({
  showPlaceName = false,
  placeName = '',
  placeData = null,
  /** When true, always use the solid red brand bar + light foreground (e.g. hotel booking on gray canvas). */
  alwaysBrandBar = false,
  stickyZIndex = 10,
  /** Homepage owns the tab. Other pages keep the header's own stored tab. */
  activeFormType: controlledFormType,
  onFormTypeChange,
}) => {
  const navigate = useNavigate();

  const isControlled = controlledFormType != null && typeof onFormTypeChange === 'function';
  const [storedFormType, setStoredFormType] = useState(() => {
    return localStorage.getItem('yovo_active_form_type') || 'flight';
  });
  const [shownHomepageTab, pressHomepageTab] = useHomepageTabPress(controlledFormType, onFormTypeChange);
  const activeFormType = isControlled ? shownHomepageTab : storedFormType;
  const [regionalSettingsOpen, setRegionalSettingsOpen] = useState(false);

  const [scrolled, setScrolled] = useState(false);
  const showBrandChrome = alwaysBrandBar || scrolled;

  const [searchOpen, setSearchOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const inlineSearchContainerRef = useRef(null);
  const inlineSearchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    fetchSearchResults,
    scheduleFetchSearchResults,
    handleSearchChange,
    handleResultClick,
    resetSearchState,
  } = useHeritageDestinationSearch();

  const navigateToSearchResult = useCallback(
    (result) => {
      setSearchOpen(false);
      handleResultClick(result);
    },
    [handleResultClick],
  );
  /** 1px line below header — IntersectionObserver uses this instead of window.scrollY (fixes overflow/layout quirks on Homepage). */
  const headerScrollSentinelRef = useRef(null);

  const handleSearchOpen = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const handleSearchClose = () => {
    setSearchOpen(false);
    resetSearchState();
  };

  // Inline search uses same fetch but via its own input
  const handleInlineSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (!searchOpen) setSearchOpen(true);
    scheduleFetchSearchResults(val);
  };


  useEffect(() => {
    if (!searchOpen) return;
    const handleClickOutside = (e) => {
      const inCollapsible = searchContainerRef.current?.contains(e.target);
      const inInline = inlineSearchContainerRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inCollapsible && !inInline && !inDropdown) {
        handleSearchClose();
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') handleSearchClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [searchOpen]);

  const syncScrolledFromDocument = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const se = document.scrollingElement;
    const top = se?.scrollTop ?? window.scrollY ?? document.documentElement?.scrollTop ?? document.body?.scrollTop ?? 0;
    setScrolled(top > 80);
  }, []);

  useEffect(() => {
    const cleanups = [];

    // Always attach scroll listener for reliable detection across all browsers/layouts
    const onScroll = () => syncScrolledFromDocument();
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll, { passive: true });
    cleanups.push(() => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
    });

    // IntersectionObserver as secondary signal (handles iOS flex-layout quirks)
    const el = headerScrollSentinelRef.current;
    if (el && typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver(
        ([entry]) => { if (entry) setScrolled(!entry.isIntersecting); },
        { root: null, threshold: 0 }
      );
      io.observe(el);
      cleanups.push(() => io.disconnect());
    }

    onScroll();
    return () => cleanups.forEach(fn => fn());
  }, [syncScrolledFromDocument]);

  useEffect(() => {
    const handleStorageChange = () => {
      const savedActiveForm = localStorage.getItem('yovo_active_form_type');
      if (!isControlled && savedActiveForm && savedActiveForm !== activeFormType) {
        setStoredFormType(savedActiveForm);
      }
    };
    const handleSameTab = (e) => {
      if (!isControlled && e.detail?.key === 'yovo_active_form_type' && e.detail?.newValue) {
        setStoredFormType(e.detail.newValue);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', handleSameTab);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleSameTab);
    };
  }, [activeFormType, isControlled]);

  const formTypeToPath = { flight: '/flights', hotel: '/hotels', train: '/trains' };

  const switchToForm = (type) => {
    if (isControlled) {
      pressHomepageTab(type);
      return;
    }
    localStorage.setItem('yovo_active_form_type', type);
    setStoredFormType(type);
    window.dispatchEvent(new CustomEvent('localStorageChange', {
      detail: { key: 'yovo_active_form_type', newValue: type }
    }));
    navigate(formTypeToPath[type] || '/flights');
  };

  const showDropdown = shouldShowHeritageSearchDropdown(
    searchOpen,
    searchQuery,
    searchLoading,
    searchResults,
  );

  /** Position from `inlineSearchContainerRef` rect; when inline is not laid out (e.g. mobile), use collapsible search ref so dropdown still aligns. */
  const updateDropdownPosition = useCallback(() => {
    const inlineEl = inlineSearchContainerRef.current;
    if (!inlineEl) return;
    let rect = inlineEl.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) {
      const collapsibleEl = searchContainerRef.current;
      if (!collapsibleEl) return;
      rect = collapsibleEl.getBoundingClientRect();
    }
    if (rect.width < 1 && rect.height < 1) return;
    setDropdownPos({
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!showDropdown) return;
    updateDropdownPosition();
    const onReposition = () => updateDropdownPosition();
    window.addEventListener('scroll', onReposition, { capture: true, passive: true });
    window.addEventListener('resize', onReposition, { passive: true });
    return () => {
      window.removeEventListener('scroll', onReposition, { capture: true });
      window.removeEventListener('resize', onReposition);
    };
  }, [showDropdown, updateDropdownPosition]);

  const handleInlineSearchFocus = () => {
    if (!searchOpen) setSearchOpen(true);
    setTimeout(updateDropdownPosition, 0);
  };

  return (
    <>
      <Box className={`yovo-header ${showBrandChrome ? 'yovo-header--scrolled' : ''}`} sx={{
        position: 'sticky',
        top: 0,
        zIndex: stickyZIndex,
        background: showBrandChrome ? '#CF2048' : 'var(--yovo-grey-100)',
        width: '100%',
        py: { xs: '12px', sm: '12px', md: '20px' },
        transition: 'background 0.3s ease',
      }}>
        <Box
          className="yovo-header-toolbar"
          sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: { xs: '12px', sm: '12px', md: '14px', lg: '24px' },
          px: { xs: '16px', sm: '16px', md: '24px', lg: '32px' },
          width: '100%',
          maxWidth: {
            xs: '100%',
            sm: '100%',
            md: '1200px',
            lg: '1400px',
            xl: '1600px'
          },
          mx: 'auto',
          minWidth: 0,
        }}
        >
          {/* Left Side - Logo + Nav */}
          <Box className="yovo-header-left-cluster" sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '16px', sm: '16px', md: '14px', lg: '32px' },
            flexShrink: 1,
            minWidth: 0,
          }}>
            <Box
              component="svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 183 44"
              width="110"
              height="26"
              fill="none"
              onClick={() => navigate('/')}
              sx={{
                width: { xs: '110px', sm: '110px', md: '128px', lg: '182px' },
                height: 'auto',
                cursor: 'pointer',
                flexShrink: 0,
                '& path': { transition: 'fill 0.3s ease' },
              }}
            >
              <path d="M10.6413 30.5429L6.40048 37.8127C-1.83881 29.0081 -0.398284 18.1907 1.35189 13.8826C6.60242 0.958179 19.8297 -1.76806 27.8064 0.958178L24.6763 6.41065C19.7287 14.6903 13.1656 16.2624 10.0354 16.3059C7.06801 16.3059 6.80435 16.3211 6.40048 16.3059C4.38104 16.2297 3.0684 16.6603 3.0684 17.9214C3.0684 19.0836 3.7752 19.7389 5.39074 19.7389H10.4393C16.013 20.1428 12.8963 27.1098 10.6413 30.5429Z" fill={showBrandChrome ? '#FBFBFB' : '#CF2048'}/>
              <path d="M13.8732 32.1571L9.22852 40.0328C18.215 46.899 29.8386 43.0558 34.0677 39.6291C39.924 34.8833 44.3668 27.4115 41.8424 15.4968C40.409 8.73173 33.3944 3.0773 30.8365 2.26953C29.019 5.6016 27.0669 9.70779 26.3937 11.5589C25.1013 15.517 28.4804 16.3719 30.3316 16.3046H38.6112C40.6308 16.3046 41.0008 17.4489 40.9336 18.0211C40.9336 19.879 37.7027 19.7376 35.683 19.7376C33.6636 19.7376 31.1392 19.6691 28.0092 19.9396C21.4663 20.505 15.859 28.3202 13.8732 32.1571Z" fill={showBrandChrome ? '#FBFBFB' : '#CF2048'}/>
              <path d="M63.7352 30.5496C63.1601 30.5496 62.7145 30.3918 62.3987 30.0762C62.1018 29.742 61.9531 29.2779 61.9531 28.6839V20.2188L62.705 22.3629L55.9941 12.9232C55.7528 12.589 55.6417 12.2364 55.6602 11.8651C55.6972 11.4752 55.8549 11.1504 56.1337 10.8905C56.4121 10.612 56.802 10.4728 57.3031 10.4728C57.6741 10.4728 58.0085 10.5656 58.3053 10.7512C58.6212 10.9369 58.918 11.2246 59.1964 11.6144L64.1808 18.6873H63.4014L68.4133 11.6144C68.7106 11.206 68.9985 10.9183 69.2768 10.7512C69.5551 10.5656 69.8985 10.4728 70.307 10.4728C70.7896 10.4728 71.161 10.6027 71.4209 10.8626C71.6992 11.1039 71.8383 11.4102 71.8383 11.7815C71.8569 12.1528 71.7272 12.5426 71.4489 12.951L64.7935 22.3629L65.5174 20.2188V28.6839C65.5174 29.9277 64.9237 30.5496 63.7352 30.5496ZM79.4448 30.6053C78.0342 30.6053 76.8087 30.3175 75.769 29.742C74.7298 29.1665 73.9218 28.3497 73.3466 27.2916C72.771 26.2149 72.4831 24.9526 72.4831 23.5046C72.4831 22.4093 72.6413 21.4347 72.9567 20.5808C73.2911 19.7083 73.7641 18.9657 74.3769 18.3531C74.9896 17.7219 75.7229 17.2486 76.5765 16.933C77.4305 16.5988 78.3866 16.4318 79.4448 16.4318C80.8555 16.4318 82.081 16.7195 83.1202 17.295C84.1599 17.8705 84.9674 18.6873 85.5431 19.7454C86.1183 20.8035 86.4061 22.0566 86.4061 23.5046C86.4061 24.5998 86.2394 25.5838 85.905 26.4562C85.5896 27.3287 85.1251 28.0806 84.5129 28.7118C83.9001 29.3243 83.1668 29.7977 82.3127 30.1319C81.4587 30.4475 80.5031 30.6053 79.4448 30.6053ZM79.4448 27.9599C80.1317 27.9599 80.7349 27.7928 81.255 27.4587C81.7746 27.1245 82.1736 26.6326 82.4519 25.9829C82.7493 25.3146 82.8974 24.4885 82.8974 23.5046C82.8974 22.0195 82.5821 20.9149 81.9508 20.1909C81.3196 19.4484 80.4846 19.0771 79.4448 19.0771C78.758 19.0771 78.1548 19.2442 77.6347 19.5783C77.1151 19.8939 76.7066 20.3859 76.4098 21.0541C76.1309 21.7039 75.9918 22.5207 75.9918 23.5046C75.9918 24.9711 76.3076 26.085 76.9389 26.8461C77.5696 27.5886 78.4051 27.9599 79.4448 27.9599ZM95.9128 30.5496C95.4488 30.5496 95.0403 30.4289 94.6879 30.1876C94.3535 29.9462 94.0752 29.575 93.8524 29.0737L89.4247 18.7987C89.258 18.3902 89.1929 18.0097 89.2299 17.657C89.2855 17.3043 89.4527 17.0258 89.731 16.8216C90.0284 16.5988 90.4274 16.4874 90.9284 16.4874C91.3555 16.4874 91.6989 16.5896 91.9587 16.7937C92.219 16.9794 92.4508 17.3414 92.655 17.8797L96.4139 27.2637H95.579L99.4495 17.8519C99.6538 17.3321 99.8855 16.9794 100.145 16.7937C100.424 16.5896 100.795 16.4874 101.259 16.4874C101.668 16.4874 101.993 16.5988 102.234 16.8216C102.475 17.0258 102.624 17.3043 102.679 17.657C102.735 17.9911 102.67 18.3624 102.485 18.7708L97.9738 29.0737C97.7696 29.575 97.4907 29.9462 97.1383 30.1876C96.8039 30.4289 96.3954 30.5496 95.9128 30.5496ZM112.414 30.6053C111.003 30.6053 109.778 30.3175 108.738 29.742C107.698 29.1665 106.891 28.3497 106.316 27.2916C105.74 26.2149 105.452 24.9526 105.452 23.5046C105.452 22.4093 105.61 21.4347 105.926 20.5808C106.26 19.7083 106.733 18.9657 107.346 18.3531C107.959 17.7219 108.691 17.2486 109.545 16.933C110.4 16.5988 111.356 16.4318 112.414 16.4318C113.825 16.4318 115.05 16.7195 116.089 17.295C117.129 17.8705 117.936 18.6873 118.512 19.7454C119.087 20.8035 119.375 22.0566 119.375 23.5046C119.375 24.5998 119.208 25.5838 118.874 26.4562C118.558 27.3287 118.094 28.0806 117.482 28.7118C116.869 29.3243 116.136 29.7977 115.282 30.1319C114.428 30.4475 113.472 30.6053 112.414 30.6053ZM112.414 27.9599C113.101 27.9599 113.704 27.7928 114.224 27.4587C114.744 27.1245 115.143 26.6326 115.421 25.9829C115.718 25.3146 115.866 24.4885 115.866 23.5046C115.866 22.0195 115.551 20.9149 114.92 20.1909C114.289 19.4484 113.453 19.0771 112.414 19.0771C111.727 19.0771 111.123 19.2442 110.604 19.5783C110.084 19.8939 109.676 20.3859 109.379 21.0541C109.1 21.7039 108.961 22.5207 108.961 23.5046C108.961 24.9711 109.277 26.085 109.907 26.8461C110.539 27.5886 111.374 27.9599 112.414 27.9599ZM130.49 30.5496C129.915 30.5496 129.47 30.3918 129.154 30.0762C128.857 29.742 128.708 29.2872 128.708 28.7118V13.675H123.278C122.796 13.675 122.415 13.5451 122.137 13.2852C121.877 13.0067 121.747 12.6355 121.747 12.1714C121.747 11.6887 121.877 11.3267 122.137 11.0854C122.415 10.8255 122.796 10.6955 123.278 10.6955H137.702C138.185 10.6955 138.556 10.8255 138.816 11.0854C139.095 11.3267 139.234 11.6887 139.234 12.1714C139.234 12.6355 139.095 13.0067 138.816 13.2852C138.556 13.5451 138.185 13.675 137.702 13.675H132.272V28.7118C132.272 29.2872 132.124 29.742 131.827 30.0762C131.53 30.3918 131.084 30.5496 130.49 30.5496ZM141.934 30.5496C141.359 30.5496 140.913 30.4011 140.598 30.1041C140.3 29.7884 140.152 29.3429 140.152 28.7674V18.2417C140.152 17.6663 140.3 17.23 140.598 16.933C140.895 16.636 141.312 16.4874 141.851 16.4874C142.389 16.4874 142.807 16.636 143.104 16.933C143.401 17.23 143.549 17.6663 143.549 18.2417V19.996H143.271C143.531 18.8822 144.041 18.0375 144.802 17.462C145.564 16.8866 146.575 16.5432 147.837 16.4318C148.227 16.3946 148.534 16.4967 148.756 16.7381C148.998 16.9608 149.137 17.3136 149.174 17.7962C149.211 18.2603 149.1 18.6408 148.84 18.9379C148.599 19.2163 148.227 19.3834 147.726 19.4391L147.113 19.4948C145.981 19.6062 145.127 19.9589 144.552 20.5529C143.976 21.1284 143.688 21.9452 143.688 23.0034V28.7674C143.688 29.3429 143.54 29.7884 143.243 30.1041C142.946 30.4011 142.51 30.5496 141.934 30.5496ZM154.175 30.5217C153.618 30.5217 153.191 30.3547 152.894 30.0205C152.597 29.6864 152.449 29.2223 152.449 28.6282V18.4088C152.449 17.7962 152.597 17.3321 152.894 17.0165C153.191 16.6824 153.618 16.5153 154.175 16.5153C154.732 16.5153 155.159 16.6824 155.456 17.0165C155.772 17.3321 155.929 17.7962 155.929 18.4088V28.6282C155.929 29.2223 155.781 29.6864 155.484 30.0205C155.187 30.3547 154.751 30.5217 154.175 30.5217ZM154.175 13.9257C153.526 13.9257 153.015 13.7679 152.644 13.4522C152.291 13.1181 152.115 12.6633 152.115 12.0878C152.115 11.4938 152.291 11.039 152.644 10.7234C153.015 10.4078 153.526 10.25 154.175 10.25C154.844 10.25 155.354 10.4078 155.707 10.7234C156.06 11.039 156.236 11.4938 156.236 12.0878C156.236 12.6633 156.06 13.1181 155.707 13.4522C155.354 13.7679 154.844 13.9257 154.175 13.9257ZM162.665 35.5618C162.108 35.5618 161.681 35.4133 161.384 35.1163C161.087 34.8192 160.938 34.383 160.938 33.8075V18.2417C160.938 17.6663 161.087 17.23 161.384 16.933C161.681 16.636 162.099 16.4874 162.637 16.4874C163.194 16.4874 163.621 16.636 163.918 16.933C164.215 17.23 164.363 17.6663 164.363 18.2417V20.4137L164.057 19.5783C164.336 18.6316 164.902 17.8705 165.756 17.295C166.628 16.7195 167.64 16.4318 168.791 16.4318C169.979 16.4318 171.019 16.7195 171.91 17.295C172.819 17.8705 173.525 18.6873 174.026 19.7454C174.527 20.785 174.778 22.0381 174.778 23.5046C174.778 24.9526 174.527 26.2149 174.026 27.2916C173.525 28.3497 172.828 29.1665 171.937 29.742C171.046 30.3175 169.998 30.6053 168.791 30.6053C167.659 30.6053 166.666 30.3268 165.812 29.7699C164.958 29.1944 164.382 28.4519 164.085 27.5422H164.419V33.8075C164.419 34.383 164.261 34.8192 163.946 35.1163C163.649 35.4133 163.222 35.5618 162.665 35.5618ZM167.816 27.9599C168.503 27.9599 169.107 27.7928 169.627 27.4587C170.146 27.1245 170.545 26.6326 170.824 25.9829C171.121 25.3146 171.269 24.4885 171.269 23.5046C171.269 22.0195 170.954 20.9149 170.322 20.1909C169.691 19.4484 168.856 19.0771 167.816 19.0771C167.13 19.0771 166.526 19.2442 166.006 19.5783C165.487 19.8939 165.078 20.3859 164.781 21.0541C164.503 21.7039 164.363 22.5207 164.363 23.5046C164.363 24.9711 164.679 26.085 165.311 26.8461C165.941 27.5886 166.777 27.9599 167.816 27.9599Z" fill={showBrandChrome ? '#FBFBFB' : '#CF2048'}/>
            </Box>

            <Box className="yovo-header-nav" sx={{
              display: { xs: 'none', sm: 'none', md: 'flex' },
              gap: { sm: '10px', md: '12px', lg: '32px' },
              alignItems: 'center',
              flexShrink: 1,
              minWidth: 0,
            }}>
              <NavigationItem
                showBrandChrome={showBrandChrome}
                active={activeFormType === 'flight'}
                icon={
                  <Box component="img" src={flight_icon_header} alt="Flight" sx={{
                    width: '24px', height: '24px', flexShrink: 0,
                    filter: activeFormType === 'flight'
                      ? (showBrandChrome
                        ? 'brightness(0) saturate(100%) invert(100%)'
                        : 'brightness(0) saturate(100%) invert(20%) sepia(85%) saturate(3650%) hue-rotate(325deg) brightness(0.85) contrast(1.05)')
                      : (showBrandChrome
                        ? 'brightness(0) saturate(100%) invert(100%)'
                        : 'brightness(0) saturate(100%) invert(13%) sepia(4%) saturate(1034%) hue-rotate(202deg) brightness(95%) contrast(89%)'),
                  }} />
                }
                label="Find Flight"
                onClick={() => switchToForm('flight')}
              />
              <NavigationItem
                showBrandChrome={showBrandChrome}
                active={activeFormType === 'train'}
                icon={<TrainIcon sx={{
                  fontSize: '24px',
                  color: activeFormType === 'train'
                    ? (showBrandChrome ? '#FFFFFF' : '#CF2048')
                    : (showBrandChrome ? '#FBFBFB' : 'var(--yovo-grey-900)'),
                  transition: 'color 0.3s ease',
                }} />}
                label="Find Trains"
                onClick={() => switchToForm('train')}
              />
              <NavigationItem
                showBrandChrome={showBrandChrome}
                active={activeFormType === 'hotel'}
                icon={
                  <Box component="svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" sx={{ width: '24px', height: '24px', flexShrink: 0, '& path': { transition: 'fill 0.3s ease' } }}>
                    <path d="M20.25 10.8141C19.7772 10.6065 19.2664 10.4996 18.75 10.5H5.25C4.73368 10.4995 4.22288 10.6063 3.75 10.8136C3.08166 11.1059 2.51294 11.5865 2.11336 12.1968C1.71377 12.8071 1.50064 13.5205 1.5 14.25V19.5C1.5 19.6989 1.57902 19.8897 1.71967 20.0303C1.86032 20.171 2.05109 20.25 2.25 20.25C2.44891 20.25 2.63968 20.171 2.78033 20.0303C2.92098 19.8897 3 19.6989 3 19.5V19.125C3.00122 19.0259 3.04112 18.9312 3.11118 18.8612C3.18124 18.7911 3.27592 18.7512 3.375 18.75H20.625C20.7241 18.7512 20.8188 18.7911 20.8888 18.8612C20.9589 18.9312 20.9988 19.0259 21 19.125V19.5C21 19.6989 21.079 19.8897 21.2197 20.0303C21.3603 20.171 21.5511 20.25 21.75 20.25C21.9489 20.25 22.1397 20.171 22.2803 20.0303C22.421 19.8897 22.5 19.6989 22.5 19.5V14.25C22.4993 13.5206 22.2861 12.8073 21.8865 12.1971C21.4869 11.5869 20.9183 11.1063 20.25 10.8141ZM17.625 3.75H6.375C5.67881 3.75 5.01113 4.02656 4.51884 4.51884C4.02656 5.01113 3.75 5.67881 3.75 6.375V9.75C3.75002 9.77906 3.75679 9.80771 3.76979 9.8337C3.78278 9.85969 3.80163 9.8823 3.82486 9.89976C3.84809 9.91721 3.87505 9.92903 3.90363 9.93428C3.93221 9.93953 3.96162 9.93806 3.98953 9.93C4.39896 9.81025 4.82341 9.74964 5.25 9.75H5.44828C5.49456 9.75029 5.53932 9.73346 5.57393 9.70274C5.60855 9.67202 5.63058 9.62958 5.63578 9.58359C5.67669 9.21712 5.85115 8.87856 6.12586 8.63256C6.40056 8.38656 6.75625 8.25037 7.125 8.25H9.75C10.119 8.25003 10.475 8.38606 10.75 8.63209C11.025 8.87812 11.1997 9.21688 11.2406 9.58359C11.2458 9.62958 11.2679 9.67202 11.3025 9.70274C11.3371 9.73346 11.3818 9.75029 11.4281 9.75H12.5747C12.621 9.75029 12.6657 9.73346 12.7003 9.70274C12.735 9.67202 12.757 9.62958 12.7622 9.58359C12.8031 9.21736 12.9773 8.87899 13.2517 8.63303C13.5261 8.38706 13.8815 8.25072 14.25 8.25H16.875C17.244 8.25003 17.6 8.38606 17.875 8.63209C18.15 8.87812 18.3247 9.21688 18.3656 9.58359C18.3708 9.62958 18.3929 9.67202 18.4275 9.70274C18.4621 9.73346 18.5068 9.75029 18.5531 9.75H18.75C19.1766 9.74979 19.6011 9.81057 20.0105 9.93047C20.0384 9.93854 20.0679 9.94 20.0965 9.93473C20.1251 9.92945 20.1521 9.91759 20.1753 9.90009C20.1986 9.88258 20.2174 9.8599 20.2304 9.83385C20.2433 9.8078 20.2501 9.7791 20.25 9.75V6.375C20.25 5.67881 19.9734 5.01113 19.4812 4.51884C18.9889 4.02656 18.3212 3.75 17.625 3.75Z" fill={activeFormType === 'hotel' ? (showBrandChrome ? '#FFFFFF' : '#CF2048') : (showBrandChrome ? '#FBFBFB' : '#383A47')}/>
                  </Box>
                }
                label="Find Stays"
                onClick={() => switchToForm('hotel')}
              />
            </Box>
          </Box>

          {/* Centered place name — only visible after scrolling on heritage/guide pages */}
          {showPlaceName && placeName && scrolled && (
            <Typography
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                fontFamily: 'var(--yovo-font-primary)',
                fontWeight: 700,
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
                color: showBrandChrome ? '#FBFBFB' : '#0D0D0D',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                display: { xs: 'none', sm: 'block' },
                transition: 'color 0.3s ease, opacity 0.3s ease',
                maxWidth: '50%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {placeName}
            </Typography>
          )}

          {/* Right Side — search + globe + login/user */}
          <Box
            className="yovo-header-right-cluster"
            sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '8px', sm: '8px', md: '12px', lg: '20px' },
            ml: 'auto',
            flex: '1 1 0%',
            width: 'auto',
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            justifyContent: 'flex-end',
          }}
          >

            {/* Inline Search Bar (desktop only) — hidden when scrolled on place pages */}
            <Box
              ref={inlineSearchContainerRef}
              className="yovo-header-search-inline"
              sx={{
                flex: '1 1 0%',
                maxWidth: { md: 'min(520px, 46vw)', lg: 'min(600px, 40vw)' },
                minWidth: 0,
                height: '48px',
                alignItems: 'center',
                overflow: 'hidden',
                ...((showPlaceName && scrolled) ? { display: 'none !important' } : {}),
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                height: '100%',
                background: '#E8E9ED',
                borderRadius: '15px',
                border: 'none',
                px: '18px',
                py: '12px',
                gap: '12px',
                overflow: 'hidden',
              }}>
                <Box
                  component="img"
                  src={searchNormalIcon}
                  alt="search"
                  sx={{ width: '20px', height: '20px', flexShrink: 0 }}
                />
                <InputBase
                  className="yovo-header-search-input"
                  inputRef={inlineSearchInputRef}
                  value={searchQuery}
                  onChange={handleInlineSearchChange}
                  onFocus={handleInlineSearchFocus}
                  placeholder="Search Cities and Destinations"
                  fullWidth
                  sx={{
                    fontFamily: 'var(--yovo-font-primary)',
                    fontSize: 'var(--yovo-text-base)',
                    fontWeight: 400,
                    lineHeight: 'var(--yovo-leading-base)',
                    letterSpacing: 'var(--yovo-tracking-tight)',
                    color: 'var(--yovo-base-black)',
                    overflow: 'hidden',
                    '& input': {
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    },
                    '& input::placeholder': { color: 'var(--yovo-grey-500)', opacity: 1 },
                  }}
                />
                {searchLoading ? (
                  <CircularProgress size={18} sx={{ color: 'var(--yovo-brand-red-hover)', flexShrink: 0 }} />
                ) : (
                  <IconButton size="small" onClick={() => { if (searchQuery) fetchSearchResults(searchQuery); }} sx={{ p: '2px', flexShrink: 0 }}>
                    <ArrowForwardIcon sx={{ fontSize: 20, color: 'var(--yovo-grey-800)' }} />
                  </IconButton>
                )}
              </Box>
            </Box>

            {/* Collapsible search for mobile/tablet — hidden when scrolled on place pages */}
            <Box
              className="yovo-header-search-icon"
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
                flexShrink: 1,
                flex: searchOpen ? '1 1 0%' : '0 0 auto',
                maxWidth: searchOpen ? '100%' : 'none',
                ...((showPlaceName && scrolled) ? { display: 'none !important' } : {}),
              }}
            >
              {searchOpen ? (
                <Box ref={searchContainerRef} sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#fff',
                  borderRadius: '16px',
                  border: '1.5px solid var(--yovo-grey-300)',
                  px: '14px',
                  height: '42px',
                  width: '100%',
                  maxWidth: { xs: 'min(240px, 100%)', sm: 'min(240px, 100%)', md: 'min(340px, 100%)' },
                  minWidth: 0,
                  boxShadow: 'var(--yovo-shadow-sm)',
                  animation: 'searchFadeIn 0.2s ease-out',
                  '@keyframes searchFadeIn': {
                    from: { opacity: 0, transform: 'scaleX(0.98)' },
                    to: { opacity: 1, transform: 'scaleX(1)' },
                  },
                  transformOrigin: 'right center',
                }}>
                  <Box component="img" src={searchNormalIcon} alt="search" sx={{ width: '20px', height: '20px', mr: '10px', flexShrink: 0 }} />
                  <InputBase
                    inputRef={searchInputRef}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search heritage places..."
                    fullWidth
                    sx={{
                      fontFamily: 'var(--yovo-font-primary)',
                      fontSize: 'var(--yovo-text-md)',
                      fontWeight: 500,
                      color: 'var(--yovo-base-black)',
                      '& input::placeholder': { color: '#AEAEB8', opacity: 1 },
                    }}
                  />
                  {searchLoading ? (
                <CircularProgress size={18} sx={{ color: 'var(--yovo-brand-red-hover)', flexShrink: 0 }} />
              ) : (
                <IconButton size="small" onClick={handleSearchClose} sx={{ p: '3px', flexShrink: 0 }}>
                  <ArrowForwardIcon sx={{ fontSize: 18, color: 'var(--yovo-grey-800)' }} />
                    </IconButton>
                  )}
                </Box>
              ) : (
                <IconButton
                  onClick={handleSearchOpen}
                  sx={{
                    p: '6px',
                    transition: 'background-color 0.1s ease',
                    '&:hover': { background: showBrandChrome ? 'rgba(255,255,255,0.15)' : '#EDEDF0' },
                    '&:active': { background: showBrandChrome ? 'rgba(255,255,255,0.25)' : '#e0e0e0' }
                  }}
                >
                  <Box component="img" src={searchNormalIcon} alt="search" sx={{ width: '22px', height: '22px', filter: showBrandChrome ? 'brightness(0) invert(1)' : 'none', transition: 'filter 0.3s ease' }} />
                </IconButton>
              )}
            </Box>

            <HeritageDestinationSearchDropdown
              open={showDropdown}
              position={dropdownPos}
              dropdownRef={dropdownRef}
              searchResults={searchResults}
              searchQuery={searchQuery}
              onResultClick={navigateToSearchResult}
            />

            {/* Globe icon temporarily hidden
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setRegionalSettingsOpen(true);
              }}
              sx={{
                p: '6px',
                flexShrink: 0,
                transition: 'background-color 0.1s ease',
                '&:hover': { background: showBrandChrome ? 'rgba(255,255,255,0.15)' : '#EDEDF0' },
                '&:active': { background: showBrandChrome ? 'rgba(255,255,255,0.25)' : '#e0e0e0' }
              }}
            >
              <img src={Globalicon} alt="Global" style={{ width: '24px', height: '24px', filter: showBrandChrome ? 'brightness(0) invert(1)' : 'none', transition: 'filter 0.3s ease' }} />
            </IconButton>
            */}
            <UserAccountMenu variant="desktop" showBrandChrome={showBrandChrome} />
          </Box>
        </Box>
      </Box>

      {/* Scroll sentinel: when this leaves the viewport, switch to “scrolled” header (avoids relying on window.scrollY alone). */}
      <div
        ref={headerScrollSentinelRef}
        className="yovo-header-scroll-sentinel"
        aria-hidden
      />

      <MountWhenOpened open={regionalSettingsOpen}>
      <RegionalSettingsModal
        open={regionalSettingsOpen}
        onClose={() => setRegionalSettingsOpen(false)}
        onSave={(settings) => {
          if (window.flightCache) window.flightCache.clear();
          try {
            Object.keys(sessionStorage).forEach(key => {
              if (key.startsWith('flight_search_') || key.startsWith('flight_results_') ||
                  key.startsWith('multicity_flight_') || key.startsWith('historical_prices_')) {
                sessionStorage.removeItem(key);
              }
            });
          } catch (error) {
            console.error('Error clearing sessionStorage cache:', error);
          }
          window.dispatchEvent(new CustomEvent('currencyChanged', { detail: { currency: settings.currency } }));
        }}
      />
      </MountWhenOpened>
    </>
  );
};

export default YovoTripHeader;
